/**
 * Dialing-session state machine — orchestrates walking the visible queue and
 * placing browser calls via `useDialer` (the @twilio/voice-sdk wrapper).
 */
import { computed, reactive, readonly, ref, shallowRef, watch } from 'vue'
import { useDialer, type CallMediaStats } from '../../composables/useDialer'
import { useDialerAmdStream } from '../../composables/useDialerAmdStream'
import { APIError } from '../../services/api'
import {
  claimEntry,
  endCall,
  getDispatchStatus,
  releaseEntry,
  type CallerQueueEntry,
  type QueuedNumber,
  type ClaimResponse,
  type DispatchStatusResponse,
  type EndCallResponse,
  type LineVerdict,
} from '../../services/dialerApi'
import {
  dispositionDisplayLabel,
  dispositionLabel,
} from '../components/dialer/dialerDispositions'
import { callsUnavailableMessage, canAutoDial } from '../components/dialer/dialability'
import { mediaNoticeFor } from '../components/dialer/mediaFaults'
import { useToast } from './useToast'

export type SessionPhase =
  | 'idle'
  | 'preparing'
  | 'dialing'
  | 'live'
  | 'disposing'
  | 'error_before'
  | 'error_during'
  | 'complete'

type SessionMode = 'session' | 'oneoff'

// ---------------------------------------------------------------------------
// Module-level singleton state
// ---------------------------------------------------------------------------

const dialer = useDialer()
const amdStream = useDialerAmdStream()
const { show: showToast } = useToast()

/** One of the current person's dialable lines, plus what happened on it. */
export interface SessionLine {
  num: QueuedNumber
  outcome: string | null
}

const phase = ref<SessionPhase>('idle')
const mode = ref<SessionMode>('session')
const queue = ref<CallerQueueEntry[]>([])
const index = ref(0)
// The current person's lines for this session, in dial order — built once per
// person from the queue snapshot. The next line to dial is the first without
// an outcome; the strip renders the same array.
const lines = ref<SessionLine[]>([])
const claim = ref<ClaimResponse | null>(null)
const stats = reactive({ dialed: 0, connected: 0, skipped: 0, noAnswer: 0, failed: 0 })
const errorDetail = ref<string | null>(null) // technical detail for the error screen
const dispositionError = ref<string | null>(null) // inline error on the disposition panel
const pendingDisposition = ref<string | null>(null) // disposition screen selection (v-model)
const notes = ref('') // shared: typed during the call, carried into disposition
// Keypad sequences sent this attempt, one entry per send ("0", "204#").
const sentSequences = ref<string[]>([])
const submitting = ref(false)
// AMD said machine on the CURRENT call. Advisory only — it raises a badge on
// the dialled line and nothing else. Per-attempt.
const machineDetected = ref(false)

// Per-attempt flags (reset at the top of every advance()).
let placed = false
const answered = ref(false) // a ref so the disposition screen can render on it
let skipInProgress = false
let disposed = false
let hangupRequested = false
let attemptToken = 0 // bumped each advance(); the bridge-verify poll keys to it
const pendingVerdict = shallowRef<Promise<void> | null>(null)
const checkingLine = computed(() => pendingVerdict.value !== null)

// Three placed calls failing back-to-back reads as an outage, not
// coincidentally-adjacent bad numbers.
let consecutiveCallFailures = 0

// A media fault ends in a normal logged disposition.
// It needs its own streak or a rep whose network is
// broken would skip through the whole queue burning prospects.
let sawMediaFault = false
const mediaFaultStreak = ref(0)

const _MAX_NUMBERS_PER_PERSON = 3

// A real outcome finishes the person: we reached them, reached their machine,
// or were told to stop.
const _FINISHES_PERSON = new Set([
  'connected_interested',
  'connected_not_interested',
  'connected_callback',
  'voicemail_left',
  'do_not_call',
])

// What a call is logged as when the rep never chose: the machine the AMD badge flagged is
// a no-answer, anyone else is an answer with no disposition behind it.
function undispositioned(): string {
  if (!answered.value) return 'no_answer'
  return machineDetected.value ? 'no_answer' : 'answered'
}

// ── The run ─────────────────────────────────────────────────────────────────
let runController = new AbortController()
runController.abort() // no run in progress until start()

const currentRun = (): AbortSignal => runController.signal
const endRun = (): void => runController.abort()

function beginRun(): AbortSignal {
  runController = new AbortController() // one controller per run — never reused
  return runController.signal
}

const current = computed<CallerQueueEntry | null>(() => queue.value[index.value] ?? null)

/** The lines we may dial for this person, best-first and capped.
 */
function dialableNumbers(entry: CallerQueueEntry | null): QueuedNumber[] {
  if (!entry) return []
  const ready = (entry.numbers ?? []).filter((n) => canAutoDial(n.dialability))
  if (ready.length) return ready.slice(0, _MAX_NUMBERS_PER_PERSON)
  return [
    {
      entry_id: entry.id,
      e164: entry.prospect_e164,
      phone_type: null,
      dialability: entry.dialability,
      cooldown_until: entry.cooldown_until,
      country: entry.country,
      timezone: entry.timezone,
      last_connect: entry.last_connect,
    },
  ]
}

const currentLine = computed<SessionLine | null>(
  () => lines.value.find((l) => l.outcome === null) ?? null,
)
const currentNumber = computed<QueuedNumber | null>(() => currentLine.value?.num ?? null)
const total = computed(() => queue.value.length)
const position = computed(() => (total.value ? Math.min(index.value + 1, total.value) : 0))

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function detailFromError(e: unknown): string {
  const anyE = e as { status?: number; message?: string; data?: any }
  // FastAPI nests an HTTPException detail under `detail`, which may itself be a
  // string OR an object like {error, detail} — never String() the object (that
  // renders "[object Object]" on the error screen).
  const d = anyE?.data?.detail ?? anyE?.data
  if (d && typeof d === 'object') {
    if (d.error === 'no_phone_available') {
      return 'No outbound number is available right now — ask your team admin to add numbers.'
    }
    if (d.error === 'no_local_number') {
      return callsUnavailableMessage(typeof d.country === 'string' ? d.country : null)
    }
    if (typeof d.detail === 'string' && d.detail) return d.detail
    if (typeof d.message === 'string' && d.message) return d.message
    try {
      return JSON.stringify(d)
    } catch {
      return 'Request failed.'
    }
  }
  if (typeof d === 'string' && d) return d
  if (anyE?.status) return `${anyE.message || 'Request failed'} (HTTP ${anyE.status})`
  return String(anyE?.message || anyE || 'Unknown error')
}

function isNoPhoneAvailable(e: unknown): boolean {
  const anyE = e as { status?: number; data?: any }
  return (
    anyE?.status === 503 ||
    anyE?.data?.error === 'no_phone_available' ||
    anyE?.data?.detail?.error === 'no_phone_available'
  )
}

// Hand the claimed pool number back. Fire-and-forget: a failure only costs us
// that number until the backend's stuck-dispatch sweep, so it must never block
// the rep. Keyed on the claim's own entry id — with one entry per line, the
// person row's id names a different entry for every line after the first.
function releaseClaim(c: ClaimResponse, reason: string): void {
  releaseEntry(c.entry_id, {
    dispatch_id: c.dispatch_id,
    error_detail: reason,
    client_media_stats: measuredMediaStats(),
  }).catch((e) => {
    console.warn(
      '[dialerSession] release_entry failed',
      { entryId: c.entry_id, dispatchId: c.dispatch_id, reason },
      e,
    )
  })
}

/** Stamp the just-finished attempt onto its line (the first without an outcome). */
function recordLineOutcome(code: string): void {
  const line = currentLine.value
  if (line) line.outcome = code
}

// The one spelling of the /end payload — the disposition screen and the
// one-tap outcomes must not drift on labels or notes.
function endCallPayload(c: ClaimResponse, code: string) {
  return {
    dispatch_id: c.dispatch_id,
    ended_at: dialer.callEndedAt.value?.toISOString(),
    duration_seconds: dialer.durationSeconds.value,
    disposition_code: code,
    disposition_label: dispositionLabel(code) || dispositionDisplayLabel(code),
    notes: code === 'skipped' ? undefined : notes.value || undefined,
    client_media_stats: measuredMediaStats(),
  }
}

function measuredMediaStats(): CallMediaStats | undefined {
  const stats = dialer.mediaStats()
  return stats.samples > 0 ? stats : undefined
}

// A close-out that died in transit is sent once more; /end is idempotent per dispatch
// server-side, so a request that landed without its response is safe to repeat.
const _END_RETRY_DELAY_MS = 400

function isTransientFailure(e: unknown): boolean {
  if (e instanceof APIError) return e.status >= 500 || e.status === 408 || e.status === 429
  return e instanceof TypeError // fetch: the network dropped the request
}

async function sendEnd(c: ClaimResponse, code: string): Promise<EndCallResponse> {
  const payload = endCallPayload(c, code)
  try {
    return await endCall(c.entry_id, payload)
  } catch (e) {
    if (!isTransientFailure(e)) throw e
    await _sleep(_END_RETRY_DELAY_MS)
    return endCall(c.entry_id, payload)
  }
}

function failPlacement(detail: string): void {
  if (phase.value === 'error_before' || phase.value === 'error_during' || phase.value === 'complete') {
    return // already terminal — don't double-handle
  }
  endRun()
  errorDetail.value = detail
  // dialed counts successfully-placed calls; >0 means we got off the ground.
  phase.value = stats.dialed > 0 ? 'error_during' : 'error_before'
  const c = claim.value
  claim.value = null
  if (c) releaseClaim(c, detail) // only if one was actually claimed
}

function finishOrComplete(): void {
  endRun()
  if (mode.value === 'oneoff') reset()
  else phase.value = 'complete'
}

function tallyDisposition(code: string, redial?: boolean): void {
  consecutiveCallFailures = 0
  mediaFaultStreak.value = sawMediaFault ? mediaFaultStreak.value + 1 : 0
  // A redial rides the skip path but is not a skip — the rep didn't pass on
  // anyone, so it must not show up in their end-of-session tally.
  if (redial) return
  if (code === 'skipped') stats.skipped += 1
  // machine_detected maps to the no_answer outcome server-side; the session
  // tiles follow suit so the call isn't invisible in the run summary.
  else if (code === 'no_answer' || code === 'machine_detected') stats.noAnswer += 1
  else stats.connected += 1 // a rich code, or `answered` with no disposition behind it
}

// Human-facing copy for a per-call failure toast. The numeric code hides in
// either the SDK's "ConnectionError (31005): …" or the backend's
// "twilio_13224: …" detail string.
const _UNREACHABLE = 'Number unreachable — it may be disconnected'
const _FRIENDLY_BY_CODE: Record<string, string> = {
  '31005': _UNREACHABLE,
  '13223': _UNREACHABLE,
  '13224': _UNREACHABLE,
  '21211': _UNREACHABLE,
  '21214': _UNREACHABLE,
  '21217': _UNREACHABLE,
  '21219': _UNREACHABLE,
  '21223': _UNREACHABLE,
  '604': _UNREACHABLE, // SIP: the number does not exist
  '31000': 'Call failed',
  '31003': 'Call failed',
  '31486': 'Line busy',
  '31603': 'Call declined',
}

function failureCode(detail: string): string {
  // The SDK hands us a structured code; only a backend-detail string (bridge
  // verify) still has to be scraped.
  const structured = dialer.lastErrorCode.value
  if (structured) return String(structured)
  return (
    detail.match(/\((\d{4,5})\)/)?.[1] ||
    detail.match(/twilio[_ ](\d{4,5})/i)?.[1] ||
    detail.match(/sip[_ ](\d{3})/i)?.[1] ||
    ''
  )
}

function friendlyCallFailure(detail: string): string {
  return _FRIENDLY_BY_CODE[failureCode(detail)] || 'Call failed'
}

function failureLineCode(detail: string): 'unreachable' | 'call_failed' {
  return _FRIENDLY_BY_CODE[failureCode(detail)] === _UNREACHABLE ? 'unreachable' : 'call_failed'
}

// The SDK reports every gateway hang-up of an unanswered call with one of these
// generic codes; Twilio's report of the prospect's leg is what tells them apart.
const _GATEWAY_HANGUP_CODES = new Set([31005, 31000])
const _VERDICT_DEADLINE_MS = 3000
const _VERDICT_POLL_MS = 250

const _CALLEE_OUTCOME_TOAST: Partial<Record<LineVerdict, string>> = {
  busy: 'Line busy',
  declined: 'Call declined',
  unavailable: 'Unavailable',
  rang_out: 'No answer',
}

const _FAILURE_TOAST_BY_VERDICT: Partial<Record<LineVerdict, string>> = {
  unreachable: _UNREACHABLE,
  failed: 'Call failed',
  answered: 'Call failed', // they picked up, but the call never reached the rep
}

async function awaitLineVerdict(entryId: string, dispatchId: string): Promise<LineVerdict | null> {
  const deadline = Date.now() + _VERDICT_DEADLINE_MS
  while (Date.now() < deadline) {
    const abort = new AbortController()
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      const st = await Promise.race([
        getDispatchStatus(entryId, dispatchId, abort.signal),
        new Promise<null>((resolve) => {
          timer = setTimeout(() => resolve(null), deadline - Date.now())
        }),
      ])
      if (!st || !('line_verdict' in st)) return null
      if (st.line_verdict) return st.line_verdict
    } catch (e) {
      // A request the server refused will be refused again; the network or a 5xx may recover.
      if (e instanceof APIError && e.status < 500) return null
      console.warn('[dialerSession] line verdict poll failed', { entryId, dispatchId }, e)
    } finally {
      clearTimeout(timer)
      abort.abort()
    }
    await _sleep(Math.max(0, Math.min(_VERDICT_POLL_MS, deadline - Date.now())))
  }
  return null
}

function resolveGatewayHangup(rawDetail: string, c: ClaimResponse): void {
  skipInProgress = true // latched before any await, same as the skip path
  const token = attemptToken
  const run = currentRun()
  const settle = (async () => {
    let verdict: LineVerdict | null = null
    try {
      verdict = await awaitLineVerdict(c.entry_id, c.dispatch_id)
    } catch (e) {
      console.warn('[dialerSession] line verdict failed', { dispatchId: c.dispatch_id }, e)
    }
    // pagehide, a reset or a newer attempt took the attempt over while we waited.
    if (token !== attemptToken || disposed || phase.value !== 'dialing') return
    const toast = verdict ? _CALLEE_OUTCOME_TOAST[verdict] : undefined
    if (toast) {
      await finishEntryWith('no_answer', { toast: true, toastText: toast })
      if (run.aborted) return
      await nextTarget('no_answer', run)
      return
    }
    await failCall(rawDetail, verdict)
  })()
  pendingVerdict.value = settle
  void settle.finally(() => {
    if (pendingVerdict.value === settle) pendingVerdict.value = null
  })
}

// A placed call failed — SDK error (gateway HANGUP, dropped connection) or a
// backend-marked dispatch failure via bridge-verify. One bad number must not
// end the session: release the claim (no call log — the contact stays
// re-dialable, and Twilio's status callback lands the real error on the
// dispatch so truly invalid numbers get flagged out server-side), tell the
// rep, and move on.
async function handleCallFailure(rawDetail: string, verdict: LineVerdict | null = null): Promise<void> {
  if (
    phase.value === 'error_before' ||
    phase.value === 'error_during' ||
    phase.value === 'complete' ||
    phase.value === 'disposing' // the disposition screen owns the attempt
  ) {
    return
  }
  if (skipInProgress || disposed) return // another path owns this attempt
  await failCall(rawDetail, verdict)
}

// The release half of handleCallFailure, for a caller that already owns the attempt.
async function failCall(rawDetail: string, verdict: LineVerdict | null = null): Promise<void> {
  if (mode.value === 'oneoff') {
    // A one-off has no "next contact" — advancing would silently reset to
    // idle with no error surface. Keep the error screen.
    failPlacement(rawDetail)
    return
  }
  if (answered.value) {
    // Someone answered — a real conversation, however it ended. The rep logs
    // the outcome; the claim stays so end_call works. (The watcher routes its
    // own disconnects to 'disposing' — only bridge-verify lands here.)
    consecutiveCallFailures = 0
    dialer.hangup()
    phase.value = 'disposing'
    return
  }
  // Three failures in a row mean the setup is broken, not the line; a destination Twilio refuses
  // says nothing about the setup.
  if (verdict !== 'not_enabled') consecutiveCallFailures += 1
  if (consecutiveCallFailures >= 3) {
    failPlacement(rawDetail)
    return
  }
  skipInProgress = true // latched before any await, same as the skip path
  const entry = current.value
  const c = claim.value
  const run = currentRun()
  claim.value = null
  if (c) releaseClaim(c, rawDetail) // raw detail → dispatch.error_detail
  stats.failed += 1
  recordLineOutcome(
    verdict
      ? verdict === 'unreachable'
        ? 'unreachable'
        : verdict === 'not_enabled'
          ? 'not_available'
          : 'call_failed'
      : failureLineCode(rawDetail),
  )
  if (entry) {
    const who = entry.contact_name || entry.prospect_e164
    // Twilio refused the destination: our stored geo permissions were stale. Say so, not "failed".
    const copy =
      verdict === 'not_enabled'
        ? callsUnavailableMessage(entry.country)
        : (verdict && _FAILURE_TOAST_BY_VERDICT[verdict]) || friendlyCallFailure(rawDetail)
    showToast(`${copy} · ${who}`, 4000)
  }
  dialer.hangup()
  dialer.clearDisconnected() // zero the stale lastError before the next attempt
  if (run.aborted) return
  // The browser leg only ever sees SDK 31005, which cannot tell an unroutable
  // number from our own bad caller ID — so cascade.
  await nextTarget('call_failed', run)
}

// Logs the outcome of a call that already happened, so it is deliberately NOT
// run-gated.
async function finishEntryWith(
  code: string,
  opts?: { toast?: boolean; redial?: boolean; toastText?: string },
): Promise<void> {
  if (disposed) {
    dialer.clearDisconnected()
    return
  }
  disposed = true // latched synchronously: a racing click can't double-log
  const entry = current.value
  const c = claim.value
  const s = dialer.state.value
  if (s === 'calling' || s === 'ringing' || s === 'connected') dialer.hangup()
  let logged = true
  if (c) {
    try {
      // The claim's entry id, never the person row's — each line is its own
      // entry, and /end rejects a dispatch that doesn't belong to the entry.
      const res = await sendEnd(c, code)
      if (res.logged === false) logged = false // dispatch already terminal server-side
    } catch (e) {
      // Never block the queue on bookkeeping.
      logged = false
      console.warn(
        '[dialerSession] end_call failed',
        { entryId: c.entry_id, dispatchId: c.dispatch_id, disposition: code },
        e,
      )
    }
  }
  dialer.clearDisconnected()
  recordLineOutcome(code)
  tallyDisposition(code, opts?.redial)
  if (opts?.toast && entry) {
    const who = entry.contact_name || entry.prospect_e164
    const label = dispositionDisplayLabel(code)
    showToast(logged ? `${opts.toastText ?? label} · ${who}` : `Couldn’t log “${label}” · ${who}`)
  }
}

// ── Bridge-verify ───────────────────────────────────────────────────────────
const _BRIDGE_POLL_MS = 1500
const _BRIDGE_MAX_TRIES = 8
const _BRIDGE_QUEUED_FAIL_AFTER = 3 // ~4.5s still 'queued' ⇒ /voice never ran

// Claim-retry: a small pool can momentarily have every number in its post-call
// cooldown. Treat "no number available" as transient — keep retrying (showing
// the 'preparing' / "Connecting your line…" state) until a number frees, then
// error out if the whole window elapses.
const _CLAIM_RETRY_WINDOW_MS = 30000
const _CLAIM_RETRY_DELAY_MS = 2000

function _sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

function detailFromStatus(st: DispatchStatusResponse): string {
  return (
    st.error_detail ||
    (st.error_class ? `The call failed (${st.error_class}).` : 'The call could not be completed.')
  )
}

// While a call is dialing/live, confirm with the backend that it actually got
// off the ground — catches failures the Twilio SDK doesn't cleanly surface: a
// Dial error (e.g. 13214) the backend marked 'aborted' even though the SDK
// "connected" to the error announcement, or /voice never running (dispatch
// stuck 'queued'). Only READS; stops the instant the call ends/advances, so it
// never blocks or delays the disposition window.
async function verifyBridge(token: number, entryId: string, dispatchId: string): Promise<void> {
  for (let i = 0; i < _BRIDGE_MAX_TRIES; i++) {
    await _sleep(_BRIDGE_POLL_MS)
    if (token !== attemptToken) return // a newer attempt started
    if (phase.value !== 'dialing' && phase.value !== 'live') return // call ended/advanced
    let st: DispatchStatusResponse
    try {
      st = await getDispatchStatus(entryId, dispatchId)
    } catch (e) {
      // Retry next tick — but log it: if every poll fails we silently lose the
      // only check that catches a call the SDK thinks is fine and the backend
      // has already marked failed.
      console.warn(
        '[dialerSession] dispatch_status poll failed',
        { entryId, dispatchId, attempt: i + 1, of: _BRIDGE_MAX_TRIES },
        e,
      )
      continue
    }
    if (token !== attemptToken) return
    if (phase.value !== 'dialing' && phase.value !== 'live') return
    const failed =
      !!st.error_class ||
      st.status === 'aborted' ||
      st.status === 'failed_transient' ||
      st.status === 'failed_permanent'
    if (failed) {
      // A geo-permission refusal lands here before any verdict poll could see it.
      const verdict = st.error_class === 'destination_not_enabled' ? 'not_enabled' : null
      void handleCallFailure(detailFromStatus(st), verdict) // hangs up + advances (or dispositions)
      return
    }
    if (st.status === 'queued' && i >= _BRIDGE_QUEUED_FAIL_AFTER && phase.value === 'dialing') {
      failPlacement(
        'The dialer never received the call — its phone connection may need attention. Try again; if it keeps happening, ask an admin to check Phone settings.',
      )
      dialer.hangup()
      return
    }
  }
}

// ---------------------------------------------------------------------------
// useDialer watcher — drives connected / rang-out / error transitions
// ---------------------------------------------------------------------------

watch(
  () => [dialer.state.value, dialer.lastError.value] as const,
  ([state, lastError]) => {
    if (phase.value !== 'preparing' && phase.value !== 'dialing' && phase.value !== 'live') return
    if (skipInProgress) return // skip() owns the transition

    // Pre-placement async error (device.on('error') path, not the call() throw).
    if (!placed && !answered.value && lastError) {
      failPlacement(lastError)
      return
    }

    if (state === 'connected' && !answered.value) {
      // Someone answered (person, gatekeeper or machine — answerOnBridge means
      // 'connected' is a real pickup). Drives the LiveCallCard and the
      // disposition route. It is NOT a stats.connected bump: the tally is
      // counted from the disposition, which is the only thing that knows WHO
      // answered.
      answered.value = true
      phase.value = 'live'
      return
    }

    if (state === 'disconnected') {
      const wasPlaced = placed || answered.value
      if (!wasPlaced) {
        failPlacement(lastError || 'The call ended before it connected.')
        return
      }
      if (!answered.value) {
        // Nobody ever picked up.
        if (lastError) {
          // A gateway hang-up hides whether the line was busy, declined or rang
          // out, so Twilio's verdict decides. Anything else (a dropped
          // connection, a media error) is released and the session moves on.
          const c = claim.value
          if (c && _GATEWAY_HANGUP_CODES.has(dialer.lastErrorCode.value ?? 0)) {
            resolveGatewayHangup(lastError, c)
            return
          }
          void handleCallFailure(lastError)
          return
        }
        if (!hangupRequested) {
          // Twilio ended it (rang out / busy / rejected) — log the outcome the
          // ring already proved and keep the power-dial rhythm.
          skipInProgress = true
          const run = currentRun()
          void (async () => {
            await finishEntryWith('no_answer', { toast: true })
            if (run.aborted) return
            await nextTarget('no_answer', run)
          })()
          return
        }
        // The SDR hung up.
        pendingDisposition.value = 'no_answer'
      }
      // However the call ended, the rep logs the outcome.
      phase.value = 'disposing'
    }
  },
)

watch(
  () => dialer.mediaFault.value,
  (fault) => {
    if (fault === 'bad_connection') sawMediaFault = true
  },
)

const mediaNotice = computed(() =>
  (phase.value === 'dialing' || phase.value === 'live') && !checkingLine.value
    // +1 for the call in front of the rep: it is faulting now, so it counts
    // toward "in a row" rather than waiting to be tallied.
    ? mediaNoticeFor(dialer.mediaFault.value, mediaFaultStreak.value + 1)
    : null,
)

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

/** Move to the next line of this person, or on to the next person. The line's
 * outcome was already stamped by whichever path logged it.
 */
async function nextTarget(code: string, run: AbortSignal): Promise<void> {
  const personDone =
    // A one-off is a single call, never a cascade.
    mode.value === 'oneoff' ||
    _FINISHES_PERSON.has(code) ||
    lines.value.every((l) => l.outcome !== null)
  if (personDone) {
    index.value += 1
    lines.value = []
  }
  await advance(run)
}


async function advance(run: AbortSignal): Promise<void> {
  if (run.aborted) return // the session ended while the last outcome was in flight
  placed = false
  answered.value = false
  skipInProgress = false
  disposed = false
  hangupRequested = false
  pendingVerdict.value = null
  sawMediaFault = false
  errorDetail.value = null
  dispositionError.value = null
  pendingDisposition.value = null
  notes.value = ''
  sentSequences.value = []
  claim.value = null
  machineDetected.value = false
  const token = ++attemptToken

  if (index.value >= queue.value.length) {
    finishOrComplete()
    return
  }
  const entry = queue.value[index.value]
  if (!entry) {
    finishOrComplete()
    return
  }
  if (!lines.value.length) {
    lines.value = dialableNumbers(entry).map((num) => ({ num, outcome: null }))
  }
  const target = currentNumber.value
  if (!target) {
    // Every line of this person already carries an outcome — move on.
    index.value += 1
    lines.value = []
    await advance(run)
    return
  }

  phase.value = 'preparing'

  // 1. Claim a pool number. Retry a transient "no number available" (a small
  //    pool whose just-released number is still in its cooldown) — stay in
  //    'preparing' ("Connecting your line…") up to ~30s, then surface it.
  const claimDeadline = Date.now() + _CLAIM_RETRY_WINDOW_MS
  for (;;) {
    try {
      claim.value = await claimEntry(target.entry_id)
      break
    } catch (e) {
      if (run.aborted || token !== attemptToken || phase.value !== 'preparing') return
      if (isNoPhoneAvailable(e) && Date.now() < claimDeadline) {
        await _sleep(_CLAIM_RETRY_DELAY_MS)
        if (run.aborted || token !== attemptToken || phase.value !== 'preparing') return
        continue
      }
      failPlacement(detailFromError(e))
      return
    }
  }
  const activeClaim = claim.value
  if (!activeClaim) {
    failPlacement('Could not reserve a number to dial from.')
    return
  }
  if (run.aborted) {
    // Ended mid-claim: hand the number straight back rather than hold it until
    // the backend's stuck-dispatch sweep.
    releaseClaim(activeClaim, 'session ended')
    claim.value = null
    return
  }

  // 2. Place the call (useDialer releases the SDK + sets lastError on throw;
  //    we additionally release the claimed number).
  try {
    await dialer.call({ DispatchId: activeClaim.dispatch_id, To: activeClaim.prospect_e164 })
  } catch (e) {
    failPlacement(detailFromError(e))
    return
  }
  if (run.aborted) {
    // Ended while the call was going out — it is live on the prospect's line, so
    // hang up rather than leave it ringing behind the end screen.
    dialer.hangup()
    releaseClaim(activeClaim, 'session ended')
    claim.value = null
    return
  }

  // 3. Placed. (Guard against a connect event that beat the resolve into 'live'.)
  placed = true
  stats.dialed += 1
  if (phase.value === 'preparing') phase.value = 'dialing'
  // Background bridge-verify — confirms the call reached the backend; never
  // blocks the disposition window (stops the moment the call ends/advances).
  void verifyBridge(token, activeClaim.entry_id, activeClaim.dispatch_id)
}

async function start(
  entries: CallerQueueEntry[],
  opts?: { oneOffEntry?: CallerQueueEntry; oneOffLine?: QueuedNumber },
): Promise<void> {
  if (phase.value !== 'idle') return
  reset()
  if (opts?.oneOffEntry) {
    mode.value = 'oneoff'
    queue.value = [opts.oneOffEntry]
    if (opts.oneOffLine) {
      lines.value = [{ num: opts.oneOffLine, outcome: null }]
    }
  } else {
    mode.value = 'session'
    queue.value = [...entries] // frozen snapshot — graduations won't shift indices
  }
  if (!queue.value.length) {
    phase.value = 'idle'
    return
  }
  index.value = 0
  await advance(beginRun())
}

function hangUp(): void {
  if (phase.value !== 'dialing' && phase.value !== 'live') return
  hangupRequested = true // routes this attempt's clean end to the disposition screen
  dialer.hangup() // watcher observes 'disconnected' → 'disposing'
}

function sendDigits(seq: string): void {
  if (phase.value !== 'live') return
  if (dialer.sendDigits(seq)) sentSequences.value.push(seq)
}

async function skip(opts?: { redial?: boolean }): Promise<void> {
  if (phase.value !== 'dialing' && phase.value !== 'live') return
  if (skipInProgress) return
  skipInProgress = true
  const run = currentRun()
  const keptNotes = notes.value
  await finishEntryWith('skipped', { toast: !opts?.redial, redial: opts?.redial })
  if (run.aborted) return
  await nextTarget('skipped', run)
  // advance() wipes notes for a fresh contact; a redial is the same conversation.
  if (opts?.redial) notes.value = keptNotes
}

/**
 * Re-dial the line we are on, after the rep saw a bad-connection warning.
 */
async function redial(): Promise<void> {
  if (phase.value !== 'dialing' && phase.value !== 'live') return
  if (skipInProgress) return
  // Splice directly after the current line, never append: on a multi-line person
  // an appended duplicate sits behind their other numbers, and the redial would
  // dial one of those instead.
  const i = lines.value.findIndex((l) => l.outcome === null)
  if (i !== -1) lines.value.splice(i + 1, 0, { num: lines.value[i].num, outcome: null })
  await skip({ redial: true })
}

// The footer's one-tap outcomes: log the common dispositions without opening the
// disposition screen.
async function quickDispose(code: 'no_answer' | 'gatekeeper'): Promise<void> {
  if (phase.value !== 'dialing' && phase.value !== 'live') return
  if (skipInProgress) return
  skipInProgress = true
  const run = currentRun()
  await finishEntryWith(code, { toast: true })
  if (run.aborted) return
  await nextTarget(code, run)
}

// ── Answering machine detection ─────────────────────────────────────────────

// The verdict never acts on the call. Detection is imperfect and the footer
// already carries Hang up and the quick outcomes, so the badge informs the
// choice the rep was going to make anyway rather than replacing it.
watch(
  () => amdStream.lastVerdict.value,
  (verdict) => {
    if (!verdict || verdict.answered_by !== 'machine_start') return
    // `unknown` and `fax` are recorded server-side but never surfaced: a badge
    // for "detection gave up" is noise the rep has to reason past.
    if (phase.value !== 'live') return
    if (verdict.dispatch_id !== claim.value?.dispatch_id) return
    machineDetected.value = true
  },
)

async function flushDisposition(code?: string): Promise<boolean> {
  if (submitting.value || disposed) return true // an outcome is already in flight / logged
  const c = claim.value
  const disposition = code || pendingDisposition.value
  if (!c || !disposition) return true // nothing to log
  submitting.value = true
  dispositionError.value = null
  try {
    const res = await sendEnd(c, disposition)
    if (res.logged === false) {
      // The attempt was closed elsewhere: the person finished on another line, or the call failed.
      const who = current.value?.contact_name || current.value?.prospect_e164
      const label = dispositionDisplayLabel(disposition)
      showToast(who ? `Couldn’t log “${label}” · ${who}` : `Couldn’t log “${label}”`, 4000)
    }
    recordLineOutcome(disposition)
    tallyDisposition(disposition)
    disposed = true
    dialer.clearDisconnected()
    return true
  } catch (e) {
    dispositionError.value = detailFromError(e)
    return false
  } finally {
    submitting.value = false
  }
}

async function saveAndNext(): Promise<void> {
  if (phase.value !== 'disposing' || submitting.value || !pendingDisposition.value) return
  const run = currentRun()
  const chosen = pendingDisposition.value ?? ''
  const ok = await flushDisposition()
  if (!ok) return // stay in disposing; inline error shown
  if (run.aborted) return
  await nextTarget(chosen, run)
}

async function endSession(): Promise<void> {
  const p = phase.value
  if (p === 'idle' || p === 'complete' || p === 'error_before' || p === 'error_during') return
  // Synchronously
  endRun()

  const pending = pendingVerdict.value
  if (pending) {
    // The call already ended on its own; Twilio's verdict closes it, never 'skipped'.
    await pending
    // A failure that landed on an error screen keeps it, as End Session would there.
    if (phase.value !== 'error_before' && phase.value !== 'error_during') finishOrComplete()
    return
  }
  if (p === 'disposing') {
    // Save the selected disposition first (or what the call proved), THEN end — never
    // discard a chosen outcome.
    await flushDisposition(pendingDisposition.value || undispositioned())
    finishOrComplete()
    return
  }
  if (p === 'live' || p === 'dialing') {
    // End Session during a call never shows the disposition screen: a call someone
    // answered is logged as that, one still ringing counts as skipped.
    skipInProgress = true // suppress the disconnect→disposition watcher
    await finishEntryWith(answered.value ? undispositioned() : 'skipped')
    finishOrComplete()
    return
  }
  if (p === 'preparing') {
    const c = claim.value
    if (c && !disposed) releaseClaim(c, 'session ended')
    // A call may already be going out — useDialer latches this until the Call
    // object resolves, so it can't be left ringing.
    dialer.hangup()
    dialer.clearDisconnected()
    claim.value = null
    finishOrComplete()
  }
}

function reset(): void {
  endRun()
  pendingVerdict.value = null
  phase.value = 'idle'
  queue.value = []
  index.value = 0
  lines.value = []
  claim.value = null
  errorDetail.value = null
  dispositionError.value = null
  pendingDisposition.value = null
  notes.value = ''
  sentSequences.value = []
  submitting.value = false
  machineDetected.value = false
  stats.dialed = 0
  stats.connected = 0
  stats.skipped = 0
  stats.noAnswer = 0
  stats.failed = 0
  consecutiveCallFailures = 0
  sawMediaFault = false
  mediaFaultStreak.value = 0
  // Force the SDK back to idle so a wedged/disconnected device from a failed
  // attempt can't block the next session's first call().
  dialer.reset()
}

// ---------------------------------------------------------------------------
// Page exit
// ---------------------------------------------------------------------------

// The last chance to close out the attempt in flight; the requests ride keepalive.
function onPageHide(): void {
  const c = claim.value
  if (!c || disposed || submitting.value) return
  const p = phase.value
  // undefined = nothing to log: hand the number back. Before the prospect's line rings,
  // nothing proves /voice ever placed a call.
  let code: string | undefined
  if (p === 'preparing') code = undefined
  else if (p === 'dialing') code = dialer.state.value === 'ringing' ? 'no_answer' : undefined
  else if (p === 'live') code = undispositioned()
  else if (p === 'disposing') code = pendingDisposition.value || undispositioned()
  else return
  disposed = true
  skipInProgress = true
  endRun()
  if (code === undefined) {
    releaseEntry(c.entry_id, { dispatch_id: c.dispatch_id, error_detail: 'page closed' }).catch(() => {})
  } else {
    endCall(c.entry_id, endCallPayload(c, code)).catch(() => {})
  }
  dialer.hangup()
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', onPageHide, { capture: true })
  // A bfcache restore brings back a session whose Device the SDK already destroyed.
  window.addEventListener('pageshow', (e) => {
    if ((e as PageTransitionEvent).persisted) reset()
  })
}

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

export function useDialerSession() {
  return {
    phase: readonly(phase),
    mode: readonly(mode),
    current,
    currentNumber,
    sessionLines: readonly(lines),
    total,
    position,
    stats: readonly(stats),
    claim: readonly(claim),
    errorDetail: readonly(errorDetail),
    dispositionError: readonly(dispositionError),
    answered: readonly(answered),
    pendingDisposition, // v-model on DispositionPanel
    notes, // v-model on LiveCallCard + DispositionPanel
    sentSequences: readonly(sentSequences),
    submitting: readonly(submitting),
    machineDetected: readonly(machineDetected),
    checkingLine,

    // surfaced from useDialer for the live-call UI
    mediaFault: dialer.mediaFault,
    mediaNotice,
    durationSeconds: dialer.durationSeconds,
    isCalling: dialer.isCalling,
    isRinging: dialer.isRinging,
    isConnected: dialer.isConnected,

    start,
    skip,
    redial,
    quickDispose,
    hangUp,
    sendDigits,
    saveAndNext,
    endSession,
    reset,
  }
}
